import { normalizeFieldKey } from "./AIUtils";
import { FIELD_TYPES } from "./AIConstants";

/**
 * Build injection plan from AI JSON (dynamic keys — not hardcoded to Contents).
 * @param {object} responseData
 * @param {object} options
 * @param {string[]} options.editorFields - form keys using rich text editor
 * @param {Record<string, string>} options.fieldMap - optional explicit responseKey → formKey
 */
export const buildInjectionPlan = (responseData, options = {}) => {
  const { editorFields = [], fieldMap = {} } = options;
  const editorSet = new Set(editorFields);

  if (!responseData || typeof responseData !== "object" || Array.isArray(responseData)) {
    return [];
  }

  return Object.entries(responseData)
    .filter(([, value]) => {
      if (value === null || value === undefined) return false;
      if (typeof value === "object") return false;
      return true;
    })
    .map(([responseKey, value]) => {
      const formKey = fieldMap[responseKey] || normalizeFieldKey(responseKey);
      if (!formKey) return null;
      const type = editorSet.has(formKey)
        ? FIELD_TYPES.EDITOR
        : FIELD_TYPES.TEXT;
      return {
        responseKey,
        formKey,
        value: String(value),
        type,
      };
    })
    .filter(Boolean);
};

/**
 * Apply injection plan to React form state + optional editor callbacks.
 * @param {object} params
 * @param {Function} params.setFormData
 * @param {Array} params.plan
 * @param {Record<string, Function>} params.editorHandlers - formKey → (value) => void
 * @param {Function} params.onFieldInjected - optional (formKey, value) => void
 * @returns {string[]} injected form keys
 */
export const injectAIFields = ({
  setFormData,
  plan,
  editorHandlers = {},
  onFieldInjected,
}) => {
  const injectedKeys = [];
  const stateUpdates = {};

  const safePlan = Array.isArray(plan) ? plan : [];

  safePlan.forEach(({ formKey, value, type }) => {
    if (!formKey) return;

    if (type === FIELD_TYPES.EDITOR) {
      stateUpdates[formKey] = value;
      if (typeof editorHandlers?.[formKey] === "function") {
        editorHandlers[formKey](value);
      }
      injectedKeys.push(formKey);
      if (onFieldInjected) onFieldInjected(formKey, value);
      return;
    }

    stateUpdates[formKey] = value;
    injectedKeys.push(formKey);
    if (onFieldInjected) onFieldInjected(formKey, value);
  });

  if (Object.keys(stateUpdates).length > 0 && typeof setFormData === "function") {
    setFormData((prev) => ({ ...(prev || {}), ...stateUpdates }));
  }

  return injectedKeys;
};
